import React from 'react';
import { Box, Typography, Grid, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import footerBackground from '../../assets/imgs/img_footer.png';
import { CustomSvgIcon } from '../../components/ImagesComponents';
import { useTranslation } from 'react-i18next';
import { useAppNavigation } from '../../context/AppNavigationContext';
import { PageBlockId, Paths } from '../../shared/constants';

export default function LandingFooter() {
  const { t } = useTranslation();
  const { scrollToBlock } = useAppNavigation();

  const footerLinksTranslated = {
    mapSite: [
      {
        label: t('footer.mapSiteAboutUs'),
        path: Paths.home,
        blockId: PageBlockId.aboutUs,
      },
      {
        label: t('footer.mapSiteProjects'),
        path: Paths.home,
        blockId: PageBlockId.projects,
      },
      {
        label: t('footer.mapSiteTeams'),
        path: Paths.home,
        blockId: PageBlockId.teams,
      },
      {
        label: t('footer.mapSiteContact'),
        path: Paths.home,
        blockId: PageBlockId.contact,
      },
    ],
    workWithUs: [
      {
        label: t('footer.workWithUsJoinOurTeam'),
        path: Paths.home,
        blockId: PageBlockId.contact,
      },
    ],
    social: [
      { label: t('footer.socialFacebook'), href: 'https://www.facebook.com' },
      { label: t('footer.socialLinkedin'), href: 'https://www.linkedin.com' },
      { label: t('footer.socialInstagram'), href: 'https://www.instagram.com' },
    ],
  };

  return (
    <FooterSection>
      <Box
        width={'100%'}
        display='flex'
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        gap={{ xs: 4, md: 0 }}
      >
        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            width: { xs: '100%', md: '305px' },
          }}
        >
          <LogoContainer>
            <CustomSvgIcon name='logomark_purple' size={88} />
          </LogoContainer>
          <Typography variant='body1' color='text.secondary' gutterBottom>
            {t('footer.footerDescription')}
          </Typography>
        </Grid>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', md: 'row' }}
          flexWrap={{ xs: 'nowrap', md: 'nowrap' }}
          gap={{ xs: 4, md: 16 }}
          width={{ xs: '100%', md: 'auto' }}
        >
          <LinksList>
            <LinksTitle>{t('footer.mapSiteTitle')}</LinksTitle>
            {footerLinksTranslated.mapSite.map(link => (
              <FooterLink
                key={link.label}
                onClick={() => scrollToBlock(link.path, link.blockId)}
                sx={{ cursor: 'pointer' }}
              >
                {link.label}
              </FooterLink>
            ))}
          </LinksList>
          <LinksList>
            <LinksTitle>{t('footer.workWithUsTitle')}</LinksTitle>
            {footerLinksTranslated.workWithUs.map(link => (
              <FooterLink
                key={link.label}
                onClick={() => scrollToBlock(link.path, link.blockId)}
                sx={{ cursor: 'pointer' }}
              >
                {link.label}
              </FooterLink>
            ))}
          </LinksList>
          <LinksList>
            <LinksTitle>{t('footer.socialTitle')}</LinksTitle>
            {footerLinksTranslated.social.map(link => (
              <FooterLink
                key={link.label}
                href={link.href}
                target='_blank'
                rel='noopener noreferrer'
              >
                {link.label}
              </FooterLink>
            ))}
          </LinksList>
        </Box>
      </Box>
    </FooterSection>
  );
}

const FooterSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  minHeight: '415px',
  background: `url(${footerBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: theme.palette.text.secondary,
  padding: '60px 86px',
  [theme.breakpoints.down('md')]: {
    padding: '40px 16px',
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontSize: '12px',
  fontWeight: 300,
  fontFamily: 'Outfit, sans-serif',
  textTransform: 'uppercase',
  transition: 'color 0.3s ease',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 0,
  textAlign: 'left',
  '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const LinksTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const LinksList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    minWidth: '120px',
  },
}));
